import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Pagination,
  TextField,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CollectionsIcon from "@mui/icons-material/Collections";
import ImageIcon from "@mui/icons-material/Image";
import SearchIcon from "@mui/icons-material/Search";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { createAxios } from "../../createAxios";
import ConfirmDelete from "../ConfirmDelete";
import {
  useDeleteCollectionMutation,
  useSliderCollectionsQuery,
  useToggleSliderMutation,
} from "../../queries/useToeicQueries";

const searchFieldSx = {
  mb: 0,
  "& .MuiOutlinedInput-root": {
    height: 44,
    borderRadius: "999px",
    bgcolor: "var(--toeic-surface)",
    boxShadow: "none",
    "& fieldset": { borderColor: "var(--toeic-line)", borderStyle: "dotted" },
    "&:hover fieldset": { borderColor: "var(--toeic-sky)" },
    "&.Mui-focused fieldset": { borderColor: "var(--toeic-sky)", borderWidth: 1.5 },
  },
  "& .MuiInputAdornment-root": {
    width: 30,
    height: 30,
    maxHeight: 30,
    borderRadius: "999px",
    bgcolor: "#e8f6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    mt: "0 !important",
    mr: 0.75,
  },
  "& .MuiOutlinedInput-input": { py: 0, fontSize: 14, fontWeight: 500 },
  "& .MuiInputLabel-root": { fontWeight: 500 },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--toeic-sky-deep)" },
};

const cardPalettes = [
  { bg: "#edf7ff", color: "#2196f3", border: "#b9dcff" },
  { bg: "#e8f9ed", color: "#1f9b45", border: "#a9eabc" },
  { bg: "#ffe8f0", color: "#d81b60", border: "#ffb8cf" },
  { bg: "#f0ecff", color: "#6d4aff", border: "#c6b8ff" },
  { bg: "#fff7d6", color: "#b77900", border: "#f6d365" },
  { bg: "#e8fbff", color: "#0087a5", border: "#a7e9f6" },
];

function Collections() {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [keySearch, setKeySearch] = React.useState("");
  const itemsPerPage = 6;

  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user.signin.currentUser);
  const accessToken = currentUser?.metadata.tokens.accessToken;
  const axiosJWT = createAxios(currentUser);
  const { data: contents = [] } = useSliderCollectionsQuery(keySearch);
  const deleteCollectionMutation = useDeleteCollectionMutation();
  const toggleSliderMutation = useToggleSliderMutation();

  const handleDelete = async (id) => {
    await deleteCollectionMutation.mutateAsync({
      accessToken,
      collection: id,
      axiosJWT,
    });
  };

  const handleSearchChange = (e) => {
    setKeySearch(e.target.value);
    setCurrentPage(1);
  };

  const getCollectionMeta = (collection = "") => {
    const year = collection.slice(0, 4);
    const month = collection.slice(5, 7);
    const week = collection.slice(9);
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const monthName = monthNames[parseInt(month, 10) - 1] || "Unknown month";

    return {
      label: `Week ${week || "-"} · ${monthName} ${year || ""}`,
      year: year || "-",
      month: monthName,
      week: week || "-",
    };
  };

  const handleOn = (collection) => {
    const data = { collection, activate: true };
    toggleSliderMutation.mutate(
      { accessToken, slider: data, axiosJWT },
      {
        onSuccess: () => {
          navigate("/management/collection");
        },
      }
    );
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const activeCollection = contents.find((content) => content.isActive);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = contents.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <Box sx={{ width: "100%", mt: 4, mb: 6 }}>
      <Box component="header" sx={{ maxWidth: 820 }}>
        <Chip
          size="small"
          icon={<CollectionsIcon sx={{ fontSize: "16px !important" }} />}
          label="Slider management"
          sx={{ mb: 1.5, bgcolor: "#f0ecff", color: "#6d4aff", fontWeight: 700 }}
        />
        <Typography
          component="h1"
          sx={{
            color: "var(--toeic-ink-deep)",
            fontSize: { xs: 32, md: 44 },
            fontWeight: 800,
            letterSpacing: 0,
            lineHeight: 1.05,
          }}
        >
          Slider{" "}
          <Box
            component="span"
            sx={{
              color: "var(--toeic-sky-deep)",
              background: "linear-gradient(180deg, transparent 58%, rgba(70,168,232,.2) 0)",
            }}
          >
            Collections
          </Box>
          .
        </Typography>
        <Typography sx={{ mt: 1, color: "var(--toeic-ink-soft)", fontWeight: 700, lineHeight: 1.7 }}>
          Browse weekly homepage slider groups and choose which collection is active.
        </Typography>
      </Box>

      <Box sx={{ mt: 4, display: "grid", gap: 4 }}>
        <Box aria-hidden sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
        <Box
          className="toeic-reveal"
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: "18px",
            border: "1px solid rgba(185, 220, 255, .75)",
            bgcolor: "var(--toeic-paper-grid)",
            boxShadow: "var(--toeic-shadow-card)",
            display: "grid",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
            <Typography sx={{ color: "var(--toeic-ink)", fontSize: 13, fontWeight: 700, textTransform: "uppercase" }}>
              Collection library
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip size="small" label={`${contents.length} collections`} sx={{ bgcolor: "#e8f6ff", color: "var(--toeic-sky-deep)", fontWeight: 700 }} />
              <Chip size="small" label={`${activeCollection ? 1 : 0} active`} sx={{ bgcolor: "#e8f9ed", color: "#1f9b45", fontWeight: 700 }} />
            </Box>
          </Box>

          <TextField
            label="Search Collection"
            variant="outlined"
            fullWidth
            value={keySearch}
            sx={searchFieldSx}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "var(--toeic-sky-deep)", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box aria-hidden sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
      </Box>

      <Grid container spacing={2.5} sx={{ mt: 0 }}>
        {currentItems.map((content, index) => {
          const palette = cardPalettes[index % cardPalettes.length];
          const meta = getCollectionMeta(content.slider_collection);

          return (
            <Grid item xs={12} sm={6} lg={4} key={content.slider_collection}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  borderRadius: "14px",
                  border: 0,
                  bgcolor: "var(--toeic-surface)",
                  boxShadow: "var(--toeic-shadow-card)",
                  overflow: "hidden",
                  transition: "border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    borderColor: palette.border,
                    boxShadow: "var(--toeic-shadow-hover)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Box sx={{ height: 6, bgcolor: content.isActive ? "#35c759" : palette.border }} />
                <CardActionArea
                  component={Link}
                  to={`/management/collection/${content.slider_collection}`}
                  sx={{ p: 2.25, display: "block" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "14px",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: palette.bg,
                        color: palette.color,
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 30 }} />
                    </Box>
                    <Chip
                      size="small"
                      label={content.isActive ? "Active" : "Inactive"}
                      sx={{
                        bgcolor: content.isActive ? "#e8f9ed" : "#f5f8fb",
                        color: content.isActive ? "#1f9b45" : "#5f6b7a",
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      color: "var(--toeic-ink-deep)",
                      fontWeight: 700,
                      fontSize: 19,
                      wordBreak: "break-word",
                    }}
                  >
                    Collection {content.slider_collection}
                  </Typography>
                  <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.75, color: "var(--toeic-ink-soft)" }}>
                    <CalendarMonthIcon sx={{ fontSize: 18 }} />
                    <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
                      {meta.label}
                    </Typography>
                  </Box>
                </CardActionArea>

                <Box
                  sx={{
                    px: 2.25,
                    pb: 2,
                    pt: 1,
                    borderTop: "1.5px dotted var(--toeic-line)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Button
                    component={Link}
                    to={`/management/collection/${content.slider_collection}`}
                    endIcon={<ArrowForwardIcon />}
                    sx={{ color: palette.color, fontWeight: 700 }}
                  >
                    Open
                  </Button>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <IconButton
                      onClick={() =>
                        content.isActive ? undefined : handleOn(content.slider_collection)
                      }
                      sx={{
                        color: content.isActive ? "#35c759" : "#9aa8b8",
                        bgcolor: content.isActive ? "#e8f9ed" : "#f5f8fb",
                        "&:hover": { bgcolor: content.isActive ? "#e8f9ed" : "#edf7ff" },
                      }}
                    >
                      {content.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
                    </IconButton>
                    <IconButton>
                      <ConfirmDelete
                        onDelete={() => handleDelete(content.slider_collection)}
                        color="#F44336"
                      />
                    </IconButton>
                  </Box>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {contents.length === 0 && (
        <Box
          sx={{
            p: 4,
            mt: 2,
            textAlign: "center",
            borderRadius: "14px",
            border: "1.5px dashed var(--toeic-line)",
            bgcolor: "var(--toeic-surface)",
            boxShadow: "var(--toeic-shadow-card)",
          }}
        >
          <Typography sx={{ color: "#5f6b7a" }}>
            No slider collection available
          </Typography>
        </Box>
      )}

      <Pagination
        count={Math.ceil(contents.length / itemsPerPage)}
        page={currentPage}
        onChange={handleChangePage}
        sx={{ mt: 3, display: "flex", justifyContent: "center" }}
      />
    </Box>
  );
}

export default Collections;
